import { ReceiptStatus } from "@dune/vars";
import { ContractStatusValues } from "../contracts";

export enum ClientStatusEnum {
	NEW = "NEW",
	IN_PROGRESS = "IN_PROGRESS", 
	CANCELLED = "CANCELLED",
	VALIDATED = "VALIDATED",
}

export enum ClientTypeEnum {
	USER = "USER",
	SOCIETY = "SOCIETY",
}

export enum ClientRoleEnum {
	CLIENT = "CLIENT",
	MOA = "MOA",
	CLIENT_MOA = "CLIENT_MOA",
}

export enum ClientTabType {
	ALL = "ALL",
	NEW = "NEW",
	IN_PROGRESS = "IN_PROGRESS",
	CANCELLED = "CANCELLED",
	VALIDATED = "VALIDATED",
}

export type ClientListItem = {
	id: string;
	type: ClientTypeEnum;
	ref: string;
	firstName?: string;
	lastName?: string;
	companyName?: string;
	brokers: string[];
	constructionSites: string[];
	createdAt: Date;
	status: ClientStatusEnum;
};

export type Address = {
	streetNumber: string;
	streetName: string;
	postalCode: string;
	city: string;
};

export type ContactInfo = {
	userId?: number;
	firstName: string;
	lastName: string;
	email?: string;
	mobilePhone?: string;
	address?: Address;
};

export type ClientDetailsUser = {
	id: string;
	type: ClientTypeEnum.USER;
	ref: string;
	firstName: string;
	lastName: string;
	email?: string;
	mobilePhone?: string;
	address?: Address;
	createdAt: Date;
	status: ClientStatusEnum;
	constructionSites: string[];
	brokers: string[];
};

export type ClientDetailsSociety = {
	id: string;
	type: ClientTypeEnum.SOCIETY;
	ref: string;
	siren: string;
	denomination: string;
	// isSyndic: boolean;
	address?: Address;
	contactInfo?: ContactInfo;
	createdAt: Date;
	status: ClientStatusEnum;
	constructionSites: string[];
	brokers: string[];
};

export type ClientDetails = ClientDetailsUser | ClientDetailsSociety;

export const ClientStatusLabels = {
	NEW: "Nouveau",
	IN_PROGRESS: "En cours", 
	CANCELLED: "Annulé",
	VALIDATED: "Validé",
} as const;

export type ClientStatusLabelValues = typeof ClientStatusLabels[keyof typeof ClientStatusLabels];

export const CLIENT_DETAIL_TAB_VALUES = {
	GENERAL: "general",
	CONSTRUCTION_SITES: "construction-sites",
	PROPOSALS: "proposals",
	CONTRACTS: "contracts",
	RECEIPTS: "receipts",
} as const;

export type ClientDetailTabValues = typeof CLIENT_DETAIL_TAB_VALUES[keyof typeof CLIENT_DETAIL_TAB_VALUES];

export interface ClientConstructionSite {
	id: string;
	ref: string;
	projectOwner: string;
	projectType: string;
	siteType: string;
	cost: string;
	address: string;
	status: string;
	createdAt: string;
	product: string;
	totalPremium: string;
}

export interface GetClientsQueryParams {
	page?: number;
	limit?: number;
	search?: string;
	status?: string;
	type?: string;
}

export interface GetClientConstructionSitesParams {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
} 

export interface UserClientEditFormData {
	firstName: string;
	lastName: string;
	email?: string;
	mobilePhone?: string;
	address?: {
		streetNumber: string;
		streetName: string;
		postalCode: string;
		city: string;
	};
}

export interface SocietyClientEditFormData {
	siren: string;
	denomination?: string;
	address?: {
		streetNumber: string;
		streetName: string;
		postalCode: string;
		city: string;
	};
	contactInfo?: {
		userId?: number;
		firstName: string;
		lastName: string;
		email?: string;
		mobilePhone?: string;
		address?: {
			streetNumber: string;
			streetName: string;
			postalCode: string;
			city: string;
		};
	};
}

export type EditClientFormData = UserClientEditFormData | SocietyClientEditFormData;

export interface UpdateClientDto {
	type: ClientTypeEnum;
	firstName?: string;
	lastName?: string;
	email?: string;
	mobilePhone?: string;
	siren?: string;
	denomination?: string;
	address?: {
		streetNumber: string;
		streetName: string;
		postalCode: string;
		city: string;
	};
	contactInfo?: {
		userId?: number;
		firstName: string;
		lastName: string;
		email?: string;
		mobilePhone?: string;
		address?: {
			streetNumber: string;
			streetName: string;
			postalCode: string;
			city: string;
		};
	};
}

// Types for client propositions
export interface ClientProposition {
	id: number;
	reference: string;
	status: string;
	productType: string;
	assureurName: string;
	primeTTC: number;
	createdAt: string;
	constructionSite: {
		id: number;
		address?: string;
	};
}

// Types for client contracts  
export interface ClientContract {
	id: number;
	reference: string;
	contractNumber?: string;
	status: ContractStatusValues;
	productType: string;
	assureurName: string;
	primeTTC: number;
	contractStartDate?: string;
	contractEndDate?: string;
	signedAt?: string;
	constructionSite: {
		id: number;
		address?: string;
	};
}

// Types for client receipts
export interface ClientReceipt {
	id: number;
	reference: string;
	type: string;
	productType: string;
	status: ReceiptStatus;
	issueDate: string;
	duneManagementFeesTTC?: number;
	intermediaryManagementFeesTTC?: number;
	brokerCommission?: number;
	brokerRemuneration?: number;
	proposition: {
		id: number;
		reference: string;
		constructionSite: {
			id: number;
			address?: string;
		};
	};
}

export interface GetClientPropositionsParams {
	status?: string;
	productType?: string;
	search?: string;
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
}

export interface GetClientContractsParams {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
}

export interface GetClientReceiptsParams {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
}

// Tab Props Interfaces
export interface PropositionsTabProps {
	client: import("@dune/vars").ClientDetailItem;
}

export interface ContractsTabProps {
	client: import("@dune/vars").ClientDetailItem;
}

export interface ReceiptsTabProps {
	client: import("@dune/vars").ClientDetailItem;
}

export interface ConstructionSitesTabProps {
	client: import("@dune/vars").ClientDetailItem;
}

export interface ClientsCountResult {
	ALL: number;
	NEW: number;
	IN_PROGRESS: number;
	CANCELLED: number;
	VALIDATED: number;
}
